import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ShimmerThumbnail } from "react-shimmer-effects";
import TOPBAR from "../../components/TopBar";
import SEARCHBAR from "../../components/SeachBar";
import MENUBAR from "../../components/Menu";
import FOOTER from "../../components/Footer";
import { Helmet } from "react-helmet";

export default function LugNutTorqueBrand() {
    const mainUrl = process.env.REACT_APP_EXPRESS_APP_URL;

    const { brand } = useParams();
    const navigate = useNavigate();

    const [isHidden, setIsHidden] = useState(false);
    const [showMenuSide, setShowMenuSide] = useState("");
    const [cartItems, setCartItems] = useState("");

    const [torqueData, setTorqueData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [torqueError, setTorqueError] = useState("");

    /*
     * Fetch torque data
     */
    useEffect(() => {
        fetchTorqueData();
    }, []);

    const fetchTorqueData = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${mainUrl}/api/lug-torque-chart`
            );

            if (response.data.success) {
                setTorqueData(response.data.data || []);
            }
        } catch (error) {
            console.error(
                "Error fetching torque chart:",
                error
            );
        } finally {
            setLoading(false);
        }
    };

    /*
     * Get cart items
     */
    useEffect(() => {
        const storedCartItems =
            localStorage.getItem("totalCartItems");

        if (storedCartItems) {
            try {
                const parsedCartItems =
                    JSON.parse(storedCartItems);

                const numCartItems =
                    Object.values(parsedCartItems);

                const sum = numCartItems.reduce(
                    (acc, val) => acc + Number(val),
                    0
                );

                setCartItems(sum);
            } catch (error) {
                console.error(
                    "Error parsing cart items:",
                    error
                );
            }
        }

        const timer = setTimeout(() => {
            setIsHidden(true);
        }, 4000);

        return () => clearTimeout(timer);
    }, []);

    /*
     * Menu sidebar
     */
    const setValueMenuSideBar = (value) => {
        setShowMenuSide(value);
    };

    /*
     * Current URL
     */
    const currentLink = window.location.href;
    const url = new URL(currentLink);
    const updatedUrl = url.toString();

    /*
     * Convert brand name to URL slug
     *
     * Acura -> acura
     * Land Rover -> land-rover
     */
    const createSlug = (value) => {
        return String(value || "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
    };

    /*
     * Get all records for selected brand
     */
    const selectedBrandData = useMemo(() => {
        if (!brand) {
            return [];
        }

        const selectedSlug = createSlug(brand);

        return torqueData.filter(
            (item) =>
                createSlug(item.make) ===
                selectedSlug
        );
    }, [torqueData, brand]);

    /*
     * Get actual brand name
     */
    const selectedBrandName =
        selectedBrandData.length > 0
            ? selectedBrandData[0].make
            : brand
                ? brand
                    .split("-")
                    .map(
                        (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1)
                    )
                    .join(" ")
                : "";

    /*
     * Sort brand details:
     *
     * 1. Latest year first
     * 2. Model alphabetically
     * 3. Trim alphabetically
     *
     * Every individual year is displayed.
     */
    const brandDetails = useMemo(() => {
        return [...selectedBrandData].sort(
            (a, b) => {
                const yearA = parseInt(
                    a.year,
                    10
                );

                const yearB = parseInt(
                    b.year,
                    10
                );

                /*
                 * Latest year first
                 */
                if (yearA !== yearB) {
                    return (
                        (isNaN(yearB)
                            ? -Infinity
                            : yearB) -
                        (isNaN(yearA)
                            ? -Infinity
                            : yearA)
                    );
                }

                /*
                 * Same year:
                 * sort by model
                 */
                const modelCompare =
                    String(
                        a.model || ""
                    ).localeCompare(
                        String(
                            b.model || ""
                        )
                    );

                if (modelCompare !== 0) {
                    return modelCompare;
                }

                /*
                 * Same model:
                 * sort by trim
                 */
                return String(
                    a.trim || ""
                ).localeCompare(
                    String(
                        b.trim || ""
                    )
                );
            }
        );
    }, [selectedBrandData]);

    /*
     * Get vehicle data for a specific row
     */
    const handleTorqueNavigation = async (
        e,
        item
    ) => {
        e.preventDefault();

        setTorqueError("");

        try {
            const response = await axios.get(
                `${mainUrl}/api/lug-torque-chart/resolve-vehicle`,
                {
                    params: {
                        year: item.year,
                        make: item.make,
                        model: item.model,
                        trim: item.trim,
                    },
                }
            );

            if (
                !response.data.success ||
                !response.data.data
            ) {
                setTorqueError(
                    response.data.message ||
                    "Vehicle not found"
                );

                return;
            }

            const vehicle =
                response.data.data;

            const vehicalDetails = {
                year: String(
                    vehicle.year
                ),
                make: vehicle.make,
                model: vehicle.model,
                variant: vehicle.variant,
                make_name:
                    vehicle.make_name,
                model_name:
                    vehicle.model_name,
                variant_name:
                    vehicle.variant_name,
            };

            localStorage.setItem(
                "vehical_details",
                JSON.stringify(
                    vehicalDetails
                )
            );

            window.location.href = "/";
        } catch (error) {
            console.error(
                "Error resolving torque vehicle:",
                error
            );

            setTorqueError(
                error.response?.data?.message ||
                "Unable to find this vehicle. Please try again."
            );
        }
    };

    /*
     * Go back to brand list
     */
    const handleBack = () => {
        navigate(
            "/lug-nut-torque-chart"
        );
    };

    return (
        <>
            <Helmet>
                <title>
                    {selectedBrandName
                        ? `${selectedBrandName} Lug Nut Torque Chart`
                        : "Lug Nut Torque Chart"}
                </title>

                <meta
                    name="description"
                    content={
                        selectedBrandName
                            ? `${selectedBrandName} lug nut torque specifications for different models, trims and years.`
                            : "Lug nut torque chart with proper torque specifications for different vehicles."
                    }
                />

                <link
                    rel="canonical"
                    href={updatedUrl}
                />
            </Helmet>

            <TOPBAR isHidden={isHidden} />

            <SEARCHBAR
                setValueMenuSideBar={
                    setValueMenuSideBar
                }
                cartItemsdata={cartItems}
            />

            <MENUBAR
                showMenuSide={
                    showMenuSide
                }
                setValueMenuSideBar={
                    setValueMenuSideBar
                }
            />

            <div className="parformane-bar"
                style={{ background: "#494949" }}
            >
                <div className="container d-flex justify-content-between">
                    <ul className="p-0 m-0 listing-parmofamne">
                        <li><a href="/">Home /</a></li>
                        <li ><a href="/lug-nut-torque-chart">Lug Nut Torque Chart /</a></li>
                        <li className='active-bread text-decoration-none'><a href="#">{selectedBrandName} </a></li>
                    </ul>

                </div>
            </div>

            <section className="tech-center-page">
                <div className="container">

                    {/* Page Heading */}
                    <h1 className="text-center heading mb-4">
                        {selectedBrandName}
                    </h1>

                    {/* Error */}
                    {torqueError && (
                        <div
                            className="alert alert-danger mb-4"
                            role="alert"
                        >
                            {torqueError}
                        </div>
                    )}

                    <div className="mb-3">
                        <button
                            type="button"
                            // className="btn btn-outline-secondary"
                            className="btn btn-outline-danger"
                            onClick={handleBack}
                        >
                            <i className="fa fa-arrow-left me-2"></i>
                            Back
                        </button>
                    </div>

                    {/* 
                     * Table
                     *
                     * Header is always visible.
                     * Shimmer rows are shown while loading.
                     */}
                    {!loading &&
                        selectedBrandData.length === 0 ? (
                        <div className="alert alert-warning text-center">
                            No torque chart data
                            found for{" "}
                            <strong>
                                {
                                    selectedBrandName
                                }
                            </strong>
                            .
                        </div>
                    ) : (
                        <div className="table-responsive">

                            <table className="table table-bordered table-striped align-middle">

                                {/* HEADER ALWAYS VISIBLE */}
                                <thead className="table-dark">
                                    <tr>
                                        <th>
                                            Year
                                        </th>

                                        <th>
                                            Make
                                        </th>

                                        <th>
                                            Model
                                        </th>

                                        <th>
                                            Trim
                                        </th>

                                        <th>
                                            Torque
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>

                                    {/* SHIMMER LOADING ROWS */}
                                    {loading ? (
                                        Array.from({
                                            length: 8,
                                        }).map(
                                            (_, index) => (
                                                <tr
                                                    key={
                                                        index
                                                    }
                                                >
                                                    {/* YEAR */}
                                                    <td>
                                                        <ShimmerThumbnail
                                                            height={
                                                                20
                                                            }
                                                            rounded
                                                            className="mb-0"
                                                        />
                                                    </td>

                                                    {/* MAKE */}
                                                    <td>
                                                        <ShimmerThumbnail
                                                            height={
                                                                20
                                                            }
                                                            rounded
                                                            className="mb-0"
                                                        />
                                                    </td>

                                                    {/* MODEL */}
                                                    <td>
                                                        <ShimmerThumbnail
                                                            height={
                                                                20
                                                            }
                                                            rounded
                                                            className="mb-0"
                                                        />
                                                    </td>

                                                    {/* TRIM */}
                                                    <td>
                                                        <ShimmerThumbnail
                                                            height={
                                                                20
                                                            }
                                                            rounded
                                                            className="mb-0"
                                                        />
                                                    </td>

                                                    {/* TORQUE */}
                                                    <td>
                                                        <ShimmerThumbnail
                                                            height={
                                                                20
                                                            }
                                                            rounded
                                                            className="mb-0"
                                                        />
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    ) : (
                                        /*
                                         * REAL DATA
                                         */
                                        brandDetails.map(
                                            (
                                                item,
                                                index
                                            ) => (
                                                <tr
                                                    key={`${item.make}-${item.model}-${item.trim}-${item.year}-${index}`}
                                                >

                                                    {/* YEAR */}
                                                    <td>
                                                        {
                                                            item.year
                                                        }
                                                    </td>

                                                    {/* MAKE */}
                                                    <td>
                                                        {
                                                            item.make
                                                        }
                                                    </td>

                                                    {/* MODEL */}
                                                    <td>
                                                        {
                                                            item.model
                                                        }
                                                    </td>

                                                    {/* TRIM */}
                                                    <td>
                                                        {
                                                            item.trim
                                                        }
                                                    </td>

                                                    {/* TORQUE */}
                                                    <td>
                                                        {
                                                            item.torque
                                                        }

                                                        {/* Optional vehicle selection icon */}

                                                        {/*
                                                        <a
                                                            href="/"
                                                            className="ms-2"
                                                            title="Select this vehicle"
                                                            aria-label={`Select ${item.make} ${item.model} ${item.trim} ${item.year}`}
                                                            onClick={(e) =>
                                                                handleTorqueNavigation(
                                                                    e,
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <i
                                                                className="fa fa-external-link"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </a>
                                                        */}
                                                    </td>

                                                </tr>
                                            )
                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>
                    )}

                </div>
            </section>

            <FOOTER />
        </>
    );
}







// import React, { useState, useEffect, useMemo } from "react";
// import axios from "axios";
// import { useParams, useNavigate } from "react-router-dom";
// import { ShimmerThumbnail } from "react-shimmer-effects";
// import TOPBAR from "../../components/TopBar";
// import SEARCHBAR from "../../components/SeachBar";
// import MENUBAR from "../../components/Menu";
// import FOOTER from "../../components/Footer";
// import { Helmet } from "react-helmet";

// export default function LugNutTorqueBrand() {
//     const mainUrl = process.env.REACT_APP_EXPRESS_APP_URL;

//     const { brand } = useParams();
//     const navigate = useNavigate();

//     const [isHidden, setIsHidden] = useState(false);
//     const [showMenuSide, setShowMenuSide] = useState("");
//     const [cartItems, setCartItems] = useState("");

//     const [torqueData, setTorqueData] = useState([]);
//     const [loading, setLoading] = useState(true);

//     const [torqueError, setTorqueError] = useState("");

//     /*
//      * Fetch torque data
//      */
//     useEffect(() => {
//         fetchTorqueData();
//     }, []);

//     const fetchTorqueData = async () => {
//         try {
//             setLoading(true);

//             const response = await axios.get(
//                 `${mainUrl}/api/lug-torque-chart`
//             );

//             if (response.data.success) {
//                 setTorqueData(response.data.data || []);
//             }
//         } catch (error) {
//             console.error(
//                 "Error fetching torque chart:",
//                 error
//             );
//         } finally {
//             setLoading(false);
//         }
//     };

//     /*
//      * Get cart items
//      */
//     useEffect(() => {
//         const storedCartItems =
//             localStorage.getItem("totalCartItems");

//         if (storedCartItems) {
//             try {
//                 const parsedCartItems =
//                     JSON.parse(storedCartItems);

//                 const numCartItems =
//                     Object.values(parsedCartItems);

//                 const sum = numCartItems.reduce(
//                     (acc, val) => acc + Number(val),
//                     0
//                 );

//                 setCartItems(sum);
//             } catch (error) {
//                 console.error(
//                     "Error parsing cart items:",
//                     error
//                 );
//             }
//         }

//         const timer = setTimeout(() => {
//             setIsHidden(true);
//         }, 4000);

//         return () => clearTimeout(timer);
//     }, []);

//     /*
//      * Menu sidebar
//      */
//     const setValueMenuSideBar = (value) => {
//         setShowMenuSide(value);
//     };

//     /*
//      * Current URL
//      */
//     const currentLink = window.location.href;
//     const url = new URL(currentLink);
//     const updatedUrl = url.toString();

//     /*
//      * Convert Make to URL slug
//      */
//     const createSlug = (value) => {
//         return String(value || "")
//             .trim()
//             .toLowerCase()
//             .replace(/[^a-z0-9]+/g, "-")
//             .replace(/^-+|-+$/g, "");
//     };

//     /*
//      * Find selected brand
//      */
//     const selectedBrandData = useMemo(() => {
//         if (!brand) {
//             return [];
//         }

//         const selectedSlug = createSlug(brand);

//         return torqueData.filter(
//             (item) =>
//                 createSlug(item.make) === selectedSlug
//         );
//     }, [torqueData, brand]);

//     /*
//      * Get actual brand name
//      */
//     const selectedBrandName =
//         selectedBrandData.length > 0
//             ? selectedBrandData[0].make
//             : brand
//                 ? brand
//                     .split("-")
//                     .map(
//                         (word) =>
//                             word.charAt(0).toUpperCase() +
//                             word.slice(1)
//                     )
//                     .join(" ")
//                 : "";

//     /*
//      * Group rows by:
//      *
//      * Make + Model + Trim + Torque
//      *
//      * Then combine years into a range.
//      */
//     const brandDetails = useMemo(() => {
//         const groups = {};

//         selectedBrandData.forEach((item) => {
//             const model = String(
//                 item.model || ""
//             ).trim();

//             const trim = String(
//                 item.trim || ""
//             ).trim();

//             const torque = String(
//                 item.torque || ""
//             ).trim();

//             const key = [
//                 model.toLowerCase(),
//                 trim.toLowerCase(),
//                 torque.toLowerCase(),
//             ].join("|");

//             if (!groups[key]) {
//                 groups[key] = {
//                     make: item.make,
//                     model,
//                     trim,
//                     torque,
//                     years: [],
//                 };
//             }

//             const year = parseInt(
//                 item.year,
//                 10
//             );

//             if (!isNaN(year)) {
//                 groups[key].years.push(year);
//             }
//         });

//         return Object.values(groups)
//             .map((item) => {
//                 const years = [
//                     ...new Set(item.years),
//                 ].sort((a, b) => a - b);

//                 let yearRange = "-";

//                 if (years.length > 0) {
//                     const minYear = Math.min(
//                         ...years
//                     );

//                     const maxYear = Math.max(
//                         ...years
//                     );

//                     yearRange =
//                         minYear === maxYear
//                             ? `${minYear}`
//                             : `${minYear} - ${maxYear}`;
//                 }

//                 return {
//                     ...item,
//                     yearRange,
//                 };
//             })
//             .sort((a, b) => {
//                 const modelCompare =
//                     a.model.localeCompare(
//                         b.model
//                     );

//                 if (modelCompare !== 0) {
//                     return modelCompare;
//                 }

//                 return a.trim.localeCompare(
//                     b.trim
//                 );
//             });
//     }, [selectedBrandData]);

//     /*
//      * Handle vehicle selection
//      */
//     const handleTorqueNavigation = async (item) => {
//         setTorqueError("");

//         try {
//             const response = await axios.get(
//                 `${mainUrl}/api/lug-torque-chart/resolve-vehicle`,
//                 {
//                     params: {
//                         year: item.year,
//                         make: item.make,
//                         model: item.model,
//                         trim: item.trim,
//                     },
//                 }
//             );

//             if (
//                 !response.data.success ||
//                 !response.data.data
//             ) {
//                 setTorqueError(
//                     response.data.message ||
//                     "Vehicle not found"
//                 );

//                 return;
//             }

//             const vehicle =
//                 response.data.data;

//             const vehicalDetails = {
//                 year: String(
//                     vehicle.year
//                 ),
//                 make: vehicle.make,
//                 model: vehicle.model,
//                 variant: vehicle.variant,
//                 make_name:
//                     vehicle.make_name,
//                 model_name:
//                     vehicle.model_name,
//                 variant_name:
//                     vehicle.variant_name,
//             };

//             localStorage.setItem(
//                 "vehical_details",
//                 JSON.stringify(
//                     vehicalDetails
//                 )
//             );

//             window.location.href = "/";
//         } catch (error) {
//             console.error(
//                 "Error resolving torque vehicle:",
//                 error
//             );

//             setTorqueError(
//                 error.response?.data?.message ||
//                 "Unable to find this vehicle. Please try again."
//             );
//         }
//     };

//     /*
//      * Go back to brand list
//      */
//     const handleBack = () => {
//         navigate("/lug-nut-torque-chart");
//     };

//     return (
//         <>
//             <Helmet>
//                 <title>
//                     {selectedBrandName
//                         ? `${selectedBrandName} Lug Nut Torque Chart`
//                         : "Lug Nut Torque Chart"}
//                 </title>

//                 <meta
//                     name="description"
//                     content={
//                         selectedBrandName
//                             ? `${selectedBrandName} lug nut torque specifications, models, trims and year ranges.`
//                             : "Lug nut torque chart with proper torque specifications for different vehicles."
//                     }
//                 />

//                 <link
//                     rel="canonical"
//                     href={updatedUrl}
//                 />
//             </Helmet>

//             <TOPBAR isHidden={isHidden} />

//             <SEARCHBAR
//                 setValueMenuSideBar={
//                     setValueMenuSideBar
//                 }
//                 cartItemsdata={cartItems}
//             />

//             <MENUBAR
//                 showMenuSide={showMenuSide}
//                 setValueMenuSideBar={
//                     setValueMenuSideBar
//                 }
//             />

//             <section className="tech-center-page">
//                 <div className="container">

//                     {/* Page Heading */}
//                     <h1 className="text-center heading mb-4">
//                         {selectedBrandName}
//                     </h1>

//                     {/* Error */}
//                     {torqueError && (
//                         <div
//                             className="alert alert-danger mb-4"
//                             role="alert"
//                         >
//                             {torqueError}
//                         </div>
//                     )}

//                     {/* No Data */}
//                     {!loading &&
//                         selectedBrandData.length === 0 ? (
//                         <div className="alert alert-warning text-center">
//                             No torque chart data found
//                             for{" "}
//                             <strong>
//                                 {selectedBrandName}
//                             </strong>
//                             .
//                         </div>
//                     ) : (
//                         /*
//                          * Table is rendered even while loading.
//                          * This keeps the header visible.
//                          */
//                         <div className="table-responsive">
//                             <table className="table table-bordered table-striped align-middle">

//                                 {/* ALWAYS VISIBLE HEADER */}
//                                 <thead className="table-dark">
//                                     <tr>
//                                         <th>
//                                             Make
//                                         </th>

//                                         <th>
//                                             Model
//                                         </th>

//                                         <th>
//                                             Trim
//                                         </th>

//                                         <th>
//                                             Year Range
//                                         </th>

//                                         <th>
//                                             Torque
//                                         </th>
//                                     </tr>
//                                 </thead>

//                                 <tbody>

//                                     {/* SHIMMER ROWS */}
//                                     {loading ? (
//                                         Array.from({
//                                             length: 8,
//                                         }).map(
//                                             (_, index) => (
//                                                 <tr
//                                                     key={
//                                                         index
//                                                     }
//                                                 >
//                                                     <td>
//                                                         <ShimmerThumbnail
//                                                             height={
//                                                                 20
//                                                             }
//                                                             rounded
//                                                             className="mb-0"
//                                                         />
//                                                     </td>

//                                                     <td>
//                                                         <ShimmerThumbnail
//                                                             height={
//                                                                 20
//                                                             }
//                                                             rounded
//                                                             className="mb-0"
//                                                         />
//                                                     </td>

//                                                     <td>
//                                                         <ShimmerThumbnail
//                                                             height={
//                                                                 20
//                                                             }
//                                                             rounded
//                                                             className="mb-0"
//                                                         />
//                                                     </td>

//                                                     <td>
//                                                         <ShimmerThumbnail
//                                                             height={
//                                                                 20
//                                                             }
//                                                             rounded
//                                                             className="mb-0"
//                                                         />
//                                                     </td>

//                                                     <td>
//                                                         <ShimmerThumbnail
//                                                             height={
//                                                                 20
//                                                             }
//                                                             rounded
//                                                             className="mb-0"
//                                                         />
//                                                     </td>
//                                                 </tr>
//                                             )
//                                         )
//                                     ) : (
//                                         /*
//                                          * REAL DATA
//                                          */
//                                         brandDetails.map(
//                                             (
//                                                 item,
//                                                 index
//                                             ) => (
//                                                 <tr
//                                                     key={
//                                                         index
//                                                     }
//                                                 >
//                                                     <td>
//                                                         {
//                                                             item.make
//                                                         }
//                                                     </td>

//                                                     <td>
//                                                         {
//                                                             item.model
//                                                         }
//                                                     </td>

//                                                     <td>
//                                                         {
//                                                             item.trim
//                                                         }
//                                                     </td>

//                                                     <td>
//                                                         {
//                                                             item.yearRange
//                                                         }
//                                                     </td>

//                                                     <td>
//                                                         {
//                                                             item.torque
//                                                         }
//                                                     </td>
//                                                 </tr>
//                                             )
//                                         )
//                                     )}

//                                 </tbody>
//                             </table>
//                         </div>
//                     )}

//                 </div>
//             </section>

//             <FOOTER />
//         </>
//     );
// }
